import React, { useState, useEffect, useCallback } from 'react';
import {
  Folder,
  FolderOpen,
  ChevronRight,
  ChevronDown,
  Home,
  Loader2
} from 'lucide-react';
import { apiClient } from '@/services/apiClient';
import { apiConfig } from '@/config/apiConfig';
import { BrandfolderLabel, BreadcrumbItem } from '../../types/brandfolder';

interface TreeNodeData {
  children: BrandfolderLabel[];
  loaded: boolean;
  expanded: boolean;
}

interface LabelTreeProps {
  selectedLabelId?: string;
  onSelect: (labelId: string | undefined, breadcrumbs: BreadcrumbItem[]) => void;
}

/**
 * Safely extract the label response from the API.
 * Handles both `{ data: { child_labels, ... } }` and direct `{ child_labels, ... }` formats.
 */
function parseResponse(responseData: any): { child_labels: BrandfolderLabel[] } {
  const inner = responseData?.data ?? responseData;
  const raw = inner?.child_labels;

  if (!Array.isArray(raw)) {
    return { child_labels: [] };
  }

  // Normalise: if items are strings, wrap them; otherwise take as-is
  const child_labels: BrandfolderLabel[] = raw.map((item: any) => {
    if (typeof item === 'string') {
      return { label_id: item, label_name: item };
    }
    return {
      label_id: item.label_id ?? item.id ?? '',
      label_name: item.label_name ?? item.name ?? item.label_id ?? item.id ?? ''
    };
  });

  return { child_labels };
}

export const LabelTree: React.FC<LabelTreeProps> = ({
  selectedLabelId,
  onSelect
}) => {
  const [rootLabels, setRootLabels] = useState<BrandfolderLabel[]>([]);
  const [loadingRoot, setLoadingRoot] = useState(true);
  const [nodes, setNodes] = useState<Map<string, TreeNodeData>>(new Map());
  const [loadingNodes, setLoadingNodes] = useState<Set<string>>(new Set());
  // Maps label_id → parent label_id (null = root-level)
  const [parentMap, setParentMap] = useState<Map<string, string | null>>(new Map());
  // Maps label_id → label_name for breadcrumb building
  const [nameMap, setNameMap] = useState<Map<string, string>>(new Map());

  const baseUrl = apiConfig.endpoints.assetManagement.impact.brandfolder.list;

  // Fetch root labels on mount
  useEffect(() => {
    const fetchRoot = async () => {
      setLoadingRoot(true);
      try {
        const response = await apiClient.get(baseUrl, {
          params: { page_number: 1, page_size: 100 }
        });

        const { child_labels } = parseResponse(response.data);
        setRootLabels(child_labels);

        const newParentMap = new Map<string, string | null>();
        const newNameMap = new Map<string, string>();
        child_labels.forEach(l => {
          newParentMap.set(l.label_id, null);
          newNameMap.set(l.label_id, l.label_name);
        });
        setParentMap(newParentMap);
        setNameMap(newNameMap);
      } catch (err) {
        console.error('[LabelTree] Failed to fetch root labels:', err);
        setRootLabels([]);
      } finally {
        setLoadingRoot(false);
      }
    };
    fetchRoot();
  }, [baseUrl]);

  const fetchChildren = useCallback(async (labelId: string): Promise<BrandfolderLabel[]> => {
    try {
      const response = await apiClient.get(
        `${baseUrl}/${labelId}`,
        { params: { page_number: 1, page_size: 100 } }
      );

      const { child_labels } = parseResponse(response.data);
      return child_labels;
    } catch (err) {
      console.error(`[LabelTree] Failed to fetch children for ${labelId}:`, err);
      return [];
    }
  }, [baseUrl]);

  // Expand a node (fetch children if not loaded yet)
  const expandNode = useCallback(async (label: BrandfolderLabel) => {
    const existing = nodes.get(label.label_id);

    if (existing?.loaded) {
      // Toggle expand/collapse
      setNodes(prev => {
        const next = new Map(prev);
        next.set(label.label_id, { ...existing, expanded: !existing.expanded });
        return next;
      });
      return;
    }

    // First expand — fetch children
    setLoadingNodes(prev => new Set(prev).add(label.label_id));
    const children = await fetchChildren(label.label_id);
    setLoadingNodes(prev => {
      const next = new Set(prev);
      next.delete(label.label_id);
      return next;
    });

    // Register children in parent + name maps
    setParentMap(prev => {
      const next = new Map(prev);
      children.forEach(c => next.set(c.label_id, label.label_id));
      return next;
    });
    setNameMap(prev => {
      const next = new Map(prev);
      children.forEach(c => next.set(c.label_id, c.label_name));
      return next;
    });

    setNodes(prev => {
      const next = new Map(prev);
      next.set(label.label_id, { children, loaded: true, expanded: true });
      return next;
    });
  }, [nodes, fetchChildren]);

  // Build breadcrumb path by walking parentMap from labelId to root
  const buildBreadcrumbs = useCallback((labelId: string): BreadcrumbItem[] => {
    const path: BreadcrumbItem[] = [];
    let currentId: string | null | undefined = labelId;
    let safety = 50;

    while (currentId && safety-- > 0) {
      const name = nameMap.get(currentId) || currentId;
      path.unshift({ label_id: currentId, label_name: name });
      const parent = parentMap.get(currentId);
      if (parent === null || parent === undefined) break;
      currentId = parent;
    }

    return path;
  }, [parentMap, nameMap]);

  // Click a label row → select (load assets) AND toggle expand/collapse
  const handleLabelClick = useCallback((label: BrandfolderLabel) => {
    // 1. Select this label (loads assets in the grid)
    const breadcrumbs = buildBreadcrumbs(label.label_id);
    onSelect(label.label_id, breadcrumbs);

    // 2. Toggle expand/collapse
    expandNode(label);
  }, [buildBreadcrumbs, onSelect, expandNode]);

  const handleSelectRoot = useCallback(() => {
    onSelect(undefined, []);
  }, [onSelect]);

  const renderLabel = (label: BrandfolderLabel, depth: number = 0) => {
    const node = nodes.get(label.label_id);
    const isExpanded = node?.expanded || false;
    const isLoading = loadingNodes.has(label.label_id);
    const isSelected = selectedLabelId === label.label_id;
    const children = node?.children || [];
    const displayName = label.label_name || label.label_id || 'Unnamed';

    return (
      <div key={label.label_id || `label-${depth}`}>
        <div
          className={`
            flex items-center py-1.5 rounded-md cursor-pointer text-sm transition-colors
            ${isSelected
              ? 'bg-green-50 text-green-800 font-medium'
              : 'text-gray-700 hover:bg-gray-100'
            }
          `}
          style={{ paddingLeft: `${8 + depth * 16}px`, paddingRight: '8px' }}
          onClick={() => handleLabelClick(label)}
        >
          {/* Expand/Collapse chevron */}
          <span className="p-0.5 mr-1 flex-shrink-0">
            {isLoading ? (
              <Loader2 className="h-3.5 w-3.5 text-gray-400 animate-spin" />
            ) : isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-gray-500" />
            )}
          </span>

          {/* Folder icon */}
          {isExpanded ? (
            <FolderOpen className="h-4 w-4 text-yellow-500 mr-2 flex-shrink-0" />
          ) : (
            <Folder className="h-4 w-4 text-yellow-500 mr-2 flex-shrink-0" />
          )}

          {/* Label name */}
          <span className="truncate" title={displayName}>{displayName}</span>
        </div>

        {/* Render children if expanded */}
        {isExpanded && children.length > 0 && (
          <div>
            {children.map(child => renderLabel(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Root button */}
      <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
        <button
          onClick={handleSelectRoot}
          className={`flex items-center w-full px-1.5 py-1 rounded text-sm transition-colors ${
            selectedLabelId === undefined
              ? 'bg-green-50 text-green-800 font-medium'
              : 'text-gray-600 hover:bg-gray-200'
          }`}
        >
          <Home className="h-3.5 w-3.5 mr-1.5" />
          All Labels
        </button>
      </div>

      {/* Tree content */}
      <div className="flex-1 overflow-y-auto p-2">
        {loadingRoot ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
          </div>
        ) : rootLabels.length === 0 ? (
          <div className="text-center py-8 text-sm text-gray-500">
            <Folder className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            No labels found
          </div>
        ) : (
          <div className="space-y-0.5">
            {rootLabels.map(label => renderLabel(label))}
          </div>
        )}
      </div>
    </div>
  );
};
